import { useState, useCallback, useEffect, useRef } from 'react';
import usePerRoomState, { stateManager } from './usePerRoomState';
import logger from '../utils/logger';
import { formatFileSize } from '../utils/fileUtils'; // Import formatFileSize

/**
 * Hook for managing message reply functionality
 * Handles reply state, highlighting, and Matrix protocol formatting
 * Supports per-room state management
 */
export const useReply = (roomId) => {
  const [replyingTo, setReplyingTo] = useState(null);
  const { getState, setState, clearState } = usePerRoomState('reply', roomId);
  const previousRoomId = useRef(roomId);

  // Handle room switching - save current state and restore new room's state
  useEffect(() => {
    logger.debug(`[useReply] Room change detected. Previous: ${previousRoomId.current}, Current: ${roomId}, ReplyingTo:`, replyingTo);
    
    if (previousRoomId.current !== roomId) {
      // Save state for previous room if we had one
      if (previousRoomId.current && replyingTo) {
        logger.debug(`[useReply] Saving reply state for room ${previousRoomId.current}:`, replyingTo);
        // Save to previous room using the singleton state manager
        stateManager.setState('reply', previousRoomId.current, replyingTo);
      }
      
      // Clear current state first
      setReplyingTo(null);
      
      // Then restore state for new room
      if (roomId) {
        const savedState = getState();
        logger.debug(`[useReply] Restoring reply state for room ${roomId}:`, savedState);
        if (savedState) {
          setReplyingTo(savedState);
        }
      }
      
      previousRoomId.current = roomId;
    }
  }, [roomId, replyingTo, getState]);

  // Save state when replyingTo changes (but not during room switches)
  useEffect(() => {
    if (roomId && previousRoomId.current === roomId) {
      logger.debug(`[useReply] Saving current reply state for room ${roomId}:`, replyingTo);
      setState(replyingTo);
    }
  }, [roomId, replyingTo, setState]);

  /**
   * Start replying to a message
   * @param {Object} message - The message object to reply to
   * @param {string} senderName - Name of the message sender
   * @param {string} content - Content of the message being replied to
   */
  const startReply = useCallback((message, senderName, content, msgtype, filename, thumbnailUrl, rawSize) => {
    const isAttachment = ['m.image', 'm.video', 'm.audio', 'm.file'].includes(msgtype);
    // For attachments, the 'content' is a JSX object which can't be stored.
    // The reply preview for attachments uses the filename, so we can store that instead.
    // For text messages, the content is already a string.
    setReplyingTo({
      id: message.getId(),
      senderName,
      isAttachment,
      content: isAttachment ? filename : content,
      msgtype,
      filename,
      thumbnailUrl,
      size: rawSize ? formatFileSize(rawSize) : null, // Format the size here
    });
  }, []);

  /**
   * Cancel the current reply
   */
  const cancelReply = useCallback(() => {
    setReplyingTo(null);
    clearState();
  }, [clearState]);

  /**
   * Restore reply state from sessionStorage (without Matrix event)
   * Used when restoring draft after page refresh
   * @param {Object} replyData - Plain object from storage {id, senderName, content}
   */
  const restoreReplyFromStorage = useCallback((replyData) => {
    if (!replyData || !replyData.id) return;
    
    // Validate Matrix event ID format (should start with $)
    if (typeof replyData.id !== 'string' || !replyData.id.startsWith('$')) {
      logger.warn('[useReply] Invalid event ID in stored reply data', replyData);
      return;
    }
    
    logger.debug('[useReply] Restoring reply from storage', replyData);
    
    setReplyingTo({
      id: replyData.id,
      senderName: replyData.senderName || 'Unknown',
      content: replyData.content || '',
      isAttachment: replyData.isAttachment || false,
      msgtype: replyData.msgtype || null,
      filename: replyData.filename || null,
      thumbnailUrl: replyData.thumbnailUrl || null,
      size: replyData.size || null,
      restoredFromStorage: true,
    });
  }, []);

  /**
   * Get Matrix protocol content for reply
   * @param {string} messageBody - The reply message content
   * @returns {Object} Matrix message content with reply metadata
   */
  const getReplyContent = useCallback((messageBody) => {
    if (!replyingTo) return null;
    
    if (replyingTo.id) {
      return {
        body: messageBody,
        msgtype: 'm.text',
        'm.relates_to': {
          'm.in_reply_to': {
            event_id: replyingTo.id,
          },
        },
      };
    }
    
    logger.error('[useReply] Cannot create reply content - no ID');
    return null;
  }, [replyingTo]);

  return {
    replyingTo,
    startReply,
    cancelReply,
    getReplyContent,
    restoreReplyFromStorage,
  };
};

export default useReply;